/**
 * Call-environment gate — decides whether placing/receiving a WebRTC call is even POSSIBLE in
 * the current runtime, so the dialer can hard-block instead of failing silently mid-call.
 *
 * Why: iOS "Add to Home Screen" standalone web apps hit a long-standing WebKit bug where
 * getUserMedia gets no mic permission (navigator.mediaDevices can be undefined) — calls simply
 * can't work. Android installed PWAs do NOT have this problem, so we must NOT block them.
 *
 * Every function takes an injectable `win` (defaults to the real window) so the logic unit-tests
 * deterministically in plain node (callEnvironment.test.js) — no browser, no real devices.
 * CommonJS so node can require() it while webpack/babel interop still lets the .jsx import it.
 */

function _win(win) { return win || (typeof window !== 'undefined' ? window : {}); }

// iOS incl. iPadOS 13+ (which reports as MacIntel with a touch screen).
function isIOS(win) {
  var nav = _win(win).navigator || {};
  var ua = nav.userAgent || '';
  return /iP(hone|od|ad)/.test(ua) || (nav.platform === 'MacIntel' && (nav.maxTouchPoints || 0) > 1);
}

// Launched as an installed/home-screen app rather than a normal browser tab.
// iOS exposes navigator.standalone (Apple-only); everyone else uses the display-mode media query.
function isStandalone(win) {
  var w = _win(win);
  var nav = w.navigator || {};
  if (nav.standalone === true) return true;
  try {
    if (typeof w.matchMedia === 'function') {
      return ['standalone', 'fullscreen', 'minimal-ui'].some(function (m) {
        return w.matchMedia('(display-mode: ' + m + ')').matches;
      });
    }
  } catch (e) { /* matchMedia unavailable → fall through */ }
  return false;
}

// The mic API is literally absent → getUserMedia can never succeed (insecure context, stripped
// WebView, etc.). The surest "calls are impossible" signal there is.
function micUnavailable(win) {
  var nav = _win(win).navigator || {};
  return !(nav.mediaDevices && typeof nav.mediaDevices.getUserMedia === 'function');
}

/**
 * Returns a reason code when calling is IMPOSSIBLE in this environment, else null:
 *   'ios-standalone' — iOS home-screen app (WebKit mic bug); tell them to open in Safari.
 *   'no-mic'         — the mic API isn't present at all.
 * Note: Android standalone with a working mic returns null (calling works — do not block).
 */
function callBlockReason(win) {
  var w = _win(win);
  if (isIOS(w) && isStandalone(w)) return 'ios-standalone';
  if (micUnavailable(w)) return 'no-mic';
  return null;
}

function callingBlocked(win) { return callBlockReason(win) !== null; }

module.exports = {
  isIOS: isIOS,
  isStandalone: isStandalone,
  micUnavailable: micUnavailable,
  callBlockReason: callBlockReason,
  callingBlocked: callingBlocked,
};
